import type { CSSProperties, ReactNode } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";

// Draggable wrapper for a fiche row in the sidebar. We pass ref + listeners +
// attributes + a transform style through a render prop so the consumer can
// attach them all to the SAME element (the button). Applying them to separate
// elements is the common cause of "drag doesn't work" bugs — pointer events
// need to fire on the node dnd-kit is tracking.
export function DraggableFicheRow({
  ficheId,
  children,
}: {
  ficheId: string;
  children: (args: {
    // Spread all four onto the draggable element.
    ref: (el: HTMLElement | null) => void;
    listeners: ReturnType<typeof useDraggable>["listeners"];
    attributes: ReturnType<typeof useDraggable>["attributes"];
    style: CSSProperties;
    isDragging: boolean;
  }) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `fiche:${ficheId}`,
      data: { ficheId },
    });
  const style: CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    opacity: isDragging ? 0.4 : undefined,
    zIndex: isDragging ? 50 : undefined,
    touchAction: "none",
  };
  return (
    <>{children({ ref: setNodeRef, listeners, attributes, style, isDragging })}</>
  );
}

// Droppable wrapper for a folder row. Exposes `isOver` so the folder row can
// highlight itself when a fiche is hovered over it mid-drag.
export function DroppableFolder({
  folderId,
  children,
}: {
  folderId: string;
  children: (args: { setNodeRef: (el: HTMLElement | null) => void; isOver: boolean }) => ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `folder:${folderId}`,
    data: { folderId },
  });
  return <>{children({ setNodeRef, isOver })}</>;
}
